import { isPast } from "date-fns";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { useGetAuthUser } from "@/api/get-user";
import { formatTimeUntilStart } from "@/app/utils/format-time-until";
import { Card } from "@/components/card";
import { Button } from "@/components/ui/button";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { queryClient } from "@/lib/react-query";
import { cn } from "@/lib/utils";
import { PublicEvent, RoleEnum } from "@classview/shared-types";

import { useAddParticipantToEvent } from "../api/add-participant-to-event";
import { getEventQueryKey } from "../api/get-event";
import { isUserAttendingEvent } from "../api/is-user-participating-in-event";

import { AddEventToCalendarModal } from "./add-event-to-calendar-modal";
import NoLongerCanAttendModal from "./no-longer-can-attend-modal";
import { RegisterEventInterestModal } from "./register-event-interest-modal";
import { RegisterRoomSystemToEventModal } from "./register-room-system-to-event-modal";
import { RoomSystemsTable } from "./room-systems-table";

type RegisterForEventCardProps = {
  event: PublicEvent;
};

export function RegisterForEventCard({ event }: RegisterForEventCardProps) {
  const { organisation, hasPermission } = useOrganisation();
  const { data: user } = useGetAuthUser();
  const { mutateAsync: addAttendeeToEvent, isPending } =
    useAddParticipantToEvent({
      onSuccess: (data) => {
        queryClient.setQueryData(getEventQueryKey(event.id), data);
      },
      onError: (error) => {
        toast.error(error.message);
      },
    });

  const handleJoinEvent = () => {
    addAttendeeToEvent({
      eventId: event.id,
      payload: {
        participants: [{ userId: user?.sub }],
      },
      orgId: organisation.id,
    });
  };

  const isCurrentUserOrganiser = event.session.organiser.id === user?.sub;
  const isAttending =
    isUserAttendingEvent(event, user?.sub ?? "") || isCurrentUserOrganiser;

  const isFullyBooked =
    event.isCapacityCapped &&
    event.capacityLimit === event.session.attendees?.length;

  const now = new Date();
  const isSessionJoinable =
    event.session.start <= now.toISOString() &&
    event.session.end >= now.toISOString();

  const isEventPast = isPast(event.session.start);
  return (
    <Card className="relative self-start overflow-hidden">
      <Card.Content className="flex flex-col space-y-8">
        <div className="flex flex-col gap-4">
          {isAttending && (
            <>
              <span className="text-lg font-medium text-foreground">
                You're registered for this event!
              </span>
              <p className="text-sm text-muted-foreground">
                No longer able to make it? Click the button below to let the
                organiser know. Event will become joinable at the start time.
              </p>
              <div className="flex">
                <AddEventToCalendarModal event={event} />
              </div>
            </>
          )}

          {!isAttending && !isFullyBooked && (
            <>
              <span className="text-lg font-medium text-foreground">
                Register for this event
              </span>
              <p className="text-sm text-muted-foreground">
                Once you have registered for this event you will be able to
                engage other attendees.
              </p>
            </>
          )}

          {!isAttending && isFullyBooked && (
            <>
              <span className="text-lg font-medium text-foreground">
                Event is fully booked
              </span>
              <p className="text-sm text-muted-foreground">
                This event is fully booked. If you would like to attend,
                register your interest using the button below.
              </p>
            </>
          )}
        </div>

        <div className={cn("relative -mx-[14px]")}>
          <div className="absolute -left-[14px] h-7 w-7 -translate-y-1/2 rounded-full bg-card" />
          <div className="absolute -right-[14px] h-7 w-7 -translate-y-1/2 rounded-full bg-card" />
          <div className="w-full border-[length:1px_6px] border-t border-dashed border-card" />
        </div>

        <div className="flex flex-col gap-2">
          {isAttending ? (
            <>
              <Button
                variant={isSessionJoinable ? "default" : "outline"}
                disabled={!isSessionJoinable}
              >
                <Link to={`/r/${event.session.room.key}`} target="_blank">
                  Join event {formatTimeUntilStart(event.session.start)}
                </Link>
              </Button>
              {!isSessionJoinable &&
                !isCurrentUserOrganiser &&
                !isEventPast && <NoLongerCanAttendModal eventId={event.id} />}
            </>
          ) : (
            <>
              {!isCurrentUserOrganiser && !isFullyBooked && (
                <Button
                  variant="default"
                  onClick={handleJoinEvent}
                  isLoading={isPending}
                  isAnimated={isPending}
                  disabled={isPending}
                >
                  Register
                </Button>
              )}
            </>
          )}
          {!isCurrentUserOrganiser && isFullyBooked && (
            <RegisterEventInterestModal event={event} />
          )}

          {hasPermission(RoleEnum.EVENTS_CREATE) && (
            <div className="flex flex-col gap-4">
              <RegisterRoomSystemToEventModal event={event} />
              <RoomSystemsTable
                event={event}
                isOrganisationFieldShown={false}
                roomSystems={event.session.roomSystems}
              />
            </div>
          )}
        </div>
      </Card.Content>
    </Card>
  );
}
